/**
 * Pure FMV Outlook If Held projection kernel.
 *
 * Single source of truth for the outlook math behind
 *   GET /api/client/fmv-outlook/assets/:assetId   (customer asset card)
 *   GET /api/assets/:assetId/fmv-outlook          (ops asset card)
 *   GET /api/client/fmv-outlook/dashboard         (per-class aggregate)
 *
 * Like fmv-kernel.ts: no DB access, no async, no Date.now() — callers resolve
 * DB rows (age_curve brackets, book_depreciation_rate, latest snapshot) and
 * the current date, then pass plain values. Routes must NOT inline this
 * arithmetic.
 *
 * Model (reworked after the flat-line incident — prod age_curve brackets are
 * 6-year-wide steps, so a bracket-ratio forecast is flat inside any useful
 * horizon):
 *  - Market forecast median: the class's book_depreciation_rate sets the
 *    TOTAL loss over the window (endpoint honest to the annual rate), but the
 *    path is a quadratic ease-in — near-flat next quarter, steepening toward
 *    the window end — because the card's job is to warn about the upcoming
 *    drop, and raw exponential/linear decay front-loads it instead. The
 *    age_curve bracket ratio FLOORS every point — whichever says the bigger
 *    drop wins, so a real bracket boundary (Refresh Cliff) still bites.
 *  - Window: quarterly points from today to
 *    min(today + 12 months, windowStart + 60 months), where windowStart =
 *    coalesce(sku launch date, asset addition date). Assets past the 5-year
 *    life get history only. No rate AND no bracket → no forecast (never
 *    project a flat line or from $0).
 *  - Cone: linear interpolation from ×1.0 at t=0 to the caller's low/high
 *    ratios at the window end, with the upper edge CLAMPED to today's median —
 *    the card never suggests appreciation. Client routes pass the fixed
 *    symmetric CLIENT_OUTLOOK_CONE_PCT (stored fmv low/high bands are
 *    ops-internal and must never reach the client — clientDashboard.ts
 *    invariant); the ops route passes the real band ratios.
 *  - Price cliff: the single date the launch-anchored depreciation curve
 *    falls to (1 − OUTLOOK_CLIFF_LOSS_PCT) of its anchor value — a fixed
 *    calendar property of the asset (anchor date + class rate), which may be
 *    in the past. Null when no rate exists or the crossing lands beyond the
 *    window end.
 *  - Book line: anchor price decayed by the class's accounting rate from the
 *    anchor date, quarterly, over the same axis (anchor → window end).
 *    straight_line: anchor × (1 − rate × years), floored at 0.
 *    declining_balance: anchor × (1 − rate)^years (rate = fraction of
 *    remaining value per year; MACRS factor / life — see migration 673).
 */

export interface AgeCurveBracket {
  /** Inclusive lower bound, months. */
  ageMonthsMin: number;
  /** Exclusive upper bound, months (half-open [min, max) like the DB). */
  ageMonthsMax: number;
  multiplier: number;
}

export interface BookDepreciationConfig {
  curveShape: 'straight_line' | 'declining_balance';
  /** Yearly rate; see curve-specific semantics above. */
  annualRate: number;
}

export interface FmvOutlookPoint {
  /** 'YYYY-MM' (UTC). */
  month: string;
  medUsd: number;
  loUsd: number;
  hiUsd: number;
}

export interface BookLinePoint {
  month: string;
  valueUsd: number;
}

export interface RefreshCliff {
  /** 'YYYY-MM' the depreciation curve falls to 80% of its anchor value. May be in the past. */
  month: string;
}

export interface ProjectFmvOutlookInputs {
  /** Latest snapshot fmv_med, USD. */
  currentFmvMedUsd: number;
  /**
   * Asset age TODAY in months: snapshot age_months_at_calc + full months
   * elapsed since priced_at (the resolver's job — snapshots go stale).
   */
  effectiveAgeMonths: number;
  /** Active-window brackets for the asset's class + active model version. */
  brackets: AgeCurveBracket[];
  /**
   * Per-class depreciation rate driving the forecast slope (same table as the
   * book line). Null → bracket-ratio only; null AND no bracket → no forecast.
   */
  marketRate: BookDepreciationConfig | null;
  /**
   * Axis origin: coalesce(sku launch date, asset addition date). Bounds the
   * window at +60 months. Null → the 12-month cap alone applies.
   */
  windowStart: Date | null;
  /** First projected point, normally "now". */
  start: Date;
  /** Cone ratio vs median at the window end, e.g. 0.85 (low) / 1.15 (high). */
  coneLowRatioAtHorizon: number;
  coneHighRatioAtHorizon: number;
  /** Book anchor (launch list price or seller purchase price), USD. */
  bookAnchorUsd: number | null;
  /** Anchor date (sku launch / purchase date). */
  bookAnchorDate: Date | null;
  bookRate: BookDepreciationConfig | null;
}

export interface ProjectFmvOutlookResult {
  /**
   * Quarterly points, t=0 (= current FMV) through the window end (endpoint
   * always included). Empty when the window is closed or nothing drives a
   * slope.
   */
  forecast: FmvOutlookPoint[];
  /** Median delta if held to the window end (last med − current med). Null when no forecast. */
  heldDeltaUsd: number | null;
  refreshCliff: RefreshCliff | null;
  /** Anchor date through the window end, quarterly. Null when anchor/rate/date missing. */
  bookLine: BookLinePoint[] | null;
  /** Months actually projected: min(12-month cap, life remaining). 0 or negative life → no forecast. */
  horizonMonths: number;
  /** Axis origin, 'YYYY-MM': coalesce(launch, addition) — null when unknown. */
  windowStartMonth: string | null;
}

/**
 * Fixed symmetric cone half-width for CLIENT-facing forecasts (±15% at the
 * window end). Deliberately NOT derived from stored fmv low/high bands,
 * which are ops-internal.
 */
export const CLIENT_OUTLOOK_CONE_PCT = 0.15;

/** Rolling forecast cap: 1 year out. */
export const OUTLOOK_MAX_LOOKOUT_MONTHS = 12;

/** Assumed asset life from launch/addition: 5 years. */
export const OUTLOOK_ASSET_LIFE_MONTHS = 60;

/** Point spacing: quarterly. */
export const OUTLOOK_STEP_MONTHS = 3;

/** Cumulative-loss threshold that defines the Refresh Cliff marker. */
export const OUTLOOK_CLIFF_LOSS_PCT = 0.2;

const round2 = (n: number): number => Math.round(n * 100) / 100;

const monthKey = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;

const addMonths = (d: Date, m: number): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + m, 1));

/** Multiplier for an age, or null when no bracket covers it. Half-open [min, max). */
export function ageMultiplierFor(
  brackets: AgeCurveBracket[],
  ageMonths: number,
): number | null {
  const hit = brackets.find(
    (b) => ageMonths >= b.ageMonthsMin && ageMonths < b.ageMonthsMax,
  );
  return hit ? hit.multiplier : null;
}

/** Full months between two dates (UTC, floored at 0). */
export function fullMonthsBetween(from: Date, to: Date): number {
  const months =
    (to.getUTCFullYear() - from.getUTCFullYear()) * 12 +
    (to.getUTCMonth() - from.getUTCMonth());
  return Math.max(0, months);
}

/**
 * Remaining-value fraction after `months` at a yearly rate. Exported (not
 * just an internal helper of `projectFmvOutlook`'s book line) so callers
 * that need a book-value point off-grid from the kernel's own anchor-rooted
 * quarterly axis — e.g. the portfolio Outlook band's aggregate recovery
 * series, which evaluates several classes' book curves at a shared
 * "now + t" axis rather than each class's own anchor grid — reuse the SAME
 * decay formula instead of re-deriving it.
 */
export function rateDecayFraction(rate: BookDepreciationConfig, months: number): number {
  const years = months / 12;
  return rate.curveShape === 'straight_line'
    ? Math.max(0, 1 - rate.annualRate * years)
    : Math.pow(1 - rate.annualRate, years);
}

/** t=0, 3, 6, … with the exact endpoint always included. */
function quarterlySteps(horizonMonths: number): number[] {
  const steps: number[] = [];
  for (let t = 0; t <= horizonMonths; t += OUTLOOK_STEP_MONTHS) steps.push(t);
  if (steps[steps.length - 1] !== horizonMonths) steps.push(horizonMonths);
  return steps;
}

/**
 * Multiplier at `ageMonths`, flattening to the last bracket's multiplier once
 * past its `ageMonthsMax` instead of extrapolating into un-modeled territory
 * (same "flatten past the last bracket" rule `projectFmvOutlook` applies via
 * its `lastMult` fallback). Null only when `ageMonths` falls before any
 * bracket AND brackets is empty / doesn't cover age 0.
 */
function flattenedMultiplierFor(
  brackets: AgeCurveBracket[],
  ageMonths: number,
): number | null {
  const exact = ageMultiplierFor(brackets, ageMonths);
  if (exact !== null) return exact;
  if (brackets.length === 0) return null;
  const highest = brackets.reduce((max, b) => (b.ageMonthsMax > max.ageMonthsMax ? b : max));
  return ageMonths >= highest.ageMonthsMax ? highest.multiplier : null;
}

/**
 * Genuinely honest FMV projection at an arbitrary age — NO 12/60-month
 * horizon cap, NO quadratic ease-in (unlike `projectFmvOutlook`, which is
 * warning-card shaped: see this file's header re: the flat-line incident).
 * `scenarioEngine` needs a truthful value at any `monthsOut` across a
 * 12-60-month scenario horizon, so this extracts the same two decay models
 * `projectFmvOutlook` blends — age-curve bracket ratio and market rate decay,
 * whichever implies the bigger drop wins — but reports the RAW ratio at
 * `monthsOut` instead of easing into it.
 *
 * `anchor` is the USD value at age 0 (`monthsOut = 0` always returns
 * `anchor`, floored at 0). `brackets` are evaluated directly against
 * `monthsOut` as the absolute age — i.e. the caller passes an age-zeroed
 * bracket list / anchor pair (matches how `projectFmvOutlook` reads
 * `effectiveAgeMonths + t` against the same brackets array).
 *
 * @pure
 */
export function fmvAtMonth(
  anchor: number,
  rate: BookDepreciationConfig | null,
  brackets: AgeCurveBracket[],
  monthsOut: number,
): number {
  if (anchor <= 0) return 0;
  if (monthsOut <= 0) return round2(anchor);

  const baseMult = flattenedMultiplierFor(brackets, 0);
  const hasBracketSlope = baseMult !== null && baseMult > 0;

  let bracketRatio = 1;
  if (hasBracketSlope) {
    const mult = flattenedMultiplierFor(brackets, monthsOut) ?? baseMult;
    bracketRatio = mult / baseMult!;
  }

  let rateRatio = 1;
  if (rate !== null) {
    rateRatio = rateDecayFraction(rate, monthsOut);
  }

  return round2(anchor * Math.min(bracketRatio, rateRatio));
}

/** @pure */
export function projectFmvOutlook(
  inputs: ProjectFmvOutlookInputs,
): ProjectFmvOutlookResult {
  const {
    currentFmvMedUsd,
    effectiveAgeMonths,
    brackets,
    marketRate,
    windowStart,
    start,
    coneLowRatioAtHorizon,
    coneHighRatioAtHorizon,
    bookAnchorUsd,
    bookAnchorDate,
    bookRate,
  } = inputs;

  // Window end: min(now + 12mo, windowStart + 60mo).
  const lifeRemaining =
    windowStart !== null
      ? OUTLOOK_ASSET_LIFE_MONTHS - fullMonthsBetween(windowStart, start)
      : OUTLOOK_MAX_LOOKOUT_MONTHS;
  const horizonMonths = Math.min(OUTLOOK_MAX_LOOKOUT_MONTHS, lifeRemaining);

  // ----- market forecast -----------------------------------------------------
  const baseMult = ageMultiplierFor(brackets, effectiveAgeMonths);
  const hasBracketSlope = baseMult !== null && baseMult > 0;
  const canForecast =
    currentFmvMedUsd > 0 && horizonMonths > 0 && (marketRate !== null || hasBracketSlope);

  const forecast: FmvOutlookPoint[] = [];
  if (canForecast) {
    let lastMult = baseMult ?? 1;
    for (const t of quarterlySteps(horizonMonths)) {
      // Age-curve bracket ratio (flattens past the last bracket rather than
      // extrapolating into un-modeled territory). 1.0 when no bracket applies.
      let ageRatio = 1;
      if (hasBracketSlope) {
        const mult = ageMultiplierFor(brackets, effectiveAgeMonths + t) ?? lastMult;
        lastMult = mult;
        ageRatio = mult / baseMult;
      }
      const frac = horizonMonths === 0 ? 1 : t / horizonMonths;
      // Ease-in redistribution of the rate's total window loss: the endpoint
      // is exactly the annual-rate value at the horizon, but the path is
      // quadratic in time - near-flat next quarter, steepening toward the
      // window end. The card exists to warn about the upcoming drop, and raw
      // exponential/linear decay front-loads it instead.
      let rateRatio = 1;
      if (marketRate !== null) {
        const endFraction = rateDecayFraction(marketRate, horizonMonths);
        rateRatio = 1 - (1 - endFraction) * frac * frac;
      }
      // Whichever model says the bigger drop wins.
      const med = round2(currentFmvMedUsd * Math.min(ageRatio, rateRatio));
      const loRatio = 1 + (coneLowRatioAtHorizon - 1) * frac;
      const hiRatio = 1 + (coneHighRatioAtHorizon - 1) * frac;
      forecast.push({
        month: monthKey(addMonths(start, t)),
        medUsd: med,
        loUsd: round2(med * loRatio),
        // Never suggest appreciation: the upper edge is capped at today's median.
        hiUsd: round2(Math.min(med * hiRatio, currentFmvMedUsd)),
      });
    }
  }
  const heldDeltaUsd =
    forecast.length > 0
      ? round2(forecast[forecast.length - 1]!.medUsd - forecast[0]!.medUsd)
      : null;

  // ----- price cliff: anchor-based 20%-loss date ----------------------------
  // The date the depreciation curve falls to 80% of its anchor value. Anchor
  // date falls back to windowStart, the rate to the class market rate — same
  // curve family either way. straight_line: t = 0.2/r years. declining
  // balance: t = ln(0.8)/ln(1 − r) years. Null when it lands past the axis.
  let refreshCliff: RefreshCliff | null = null;
  {
    const cliffAnchor = bookAnchorDate ?? windowStart;
    const cliffRate = bookRate ?? marketRate;
    if (cliffAnchor !== null && cliffRate !== null && cliffRate.annualRate > 0) {
      const years =
        cliffRate.curveShape === 'straight_line'
          ? OUTLOOK_CLIFF_LOSS_PCT / cliffRate.annualRate
          : Math.log(1 - OUTLOOK_CLIFF_LOSS_PCT) / Math.log(1 - cliffRate.annualRate);
      const tMonths = Math.round(years * 12);
      const monthsToAxisEnd = fullMonthsBetween(cliffAnchor, start) + Math.max(0, horizonMonths);
      if (tMonths <= monthsToAxisEnd) {
        refreshCliff = { month: monthKey(addMonths(cliffAnchor, tMonths)) };
      }
    }
  }

  // ----- book depreciation line ---------------------------------------------
  let bookLine: BookLinePoint[] | null = null;
  if (bookAnchorUsd !== null && bookAnchorUsd > 0 && bookAnchorDate !== null && bookRate !== null) {
    const totalMonths =
      fullMonthsBetween(bookAnchorDate, start) + Math.max(0, horizonMonths);
    bookLine = quarterlySteps(totalMonths).map((m) => ({
      month: monthKey(addMonths(bookAnchorDate, m)),
      valueUsd: round2(bookAnchorUsd * rateDecayFraction(bookRate, m)),
    }));
  }

  const windowStartMonth = windowStart !== null ? monthKey(new Date(Date.UTC(
    windowStart.getUTCFullYear(), windowStart.getUTCMonth(), 1))) : null;

  return { forecast, heldDeltaUsd, refreshCliff, bookLine, horizonMonths, windowStartMonth };
}
